import { AppData, Lightning } from '@lightningjs/sdk';
import SignInPasswordContent from './SignInPasswordContent';
import AbstractLoginModal, {
  AbstractLoginModalTemplateSpec,
  LoginFlow,
} from '../layout/AbstractLoginModal';
import { WidgetNames } from 'config/routes';
import { LoginRadiusError } from 'types/loginRadius';
import { isFieldLevelValidationError } from 'support/loginRadiusUtils';

export interface SignInPasswordModalTemplateSpec
  extends AbstractLoginModalTemplateSpec {
  Content: {
    SignInPasswordContent: typeof SignInPasswordContent;
  } & AbstractLoginModalTemplateSpec['Content'];
}

export default class SignInPasswordModal
  extends AbstractLoginModal
  implements
    Lightning.Component.ImplementTemplateSpec<SignInPasswordModalTemplateSpec>
{
  override _showBackButton = true;

  override get loginFlow() {
    return LoginFlow.SIGN_IN;
  }

  private _SignInPasswordContent = this._Content.getByRef(
    'SignInPasswordContent',
  );

  static override _template(): Lightning.Component.Template<SignInPasswordModalTemplateSpec> {
    const { Content, ...rest } = super._template();
    return {
      Content: {
        ...Content,
        h: 1080,
        flex: {
          direction: 'column',
          justifyContent: 'center',
          alignItems: 'center',
        },
        SignInPasswordContent: {
          type: SignInPasswordContent,
          signals: {
            $onContentHovered: true,
            $onSignIn: true,
            $onForgotPassword: true,
            $openKeyboard: true,
            $resetToEmailInput: true,
          },
        },
      },
      ...rest,
    };
  }

  modalInit(data: { email: string }) {
    this._SignInPasswordContent.email = data.email;
  }

  private $onContentHovered() {
    this._setState('Content');
  }

  private async $onSignIn(email: string, password: string) {
    const isLoggedIn = await this.onLoading(
      AppData!.loginRadiusService!.loginByEmail(email, password),
    );
    if (isLoggedIn === undefined) return;

    if (isLoggedIn) {
      AppData?.modalService.open('SignInConfirmationModal');
    }
  }

  private $onForgotPassword() {
    AppData?.modalService.open(WidgetNames.FORGOT_PASSWORD_MODAL, {
      data: { email: this._SignInPasswordContent.email },
    });
  }

  override handleFieldLevelError(error: LoginRadiusError) {
    this._SignInPasswordContent.fieldLevelError = error;
  }

  override handleLoadingError(error: LoginRadiusError) {
    isFieldLevelValidationError(error)
      ? this.handleFieldLevelError(error)
      : super.handleLoadingError(error);
  }

  override _getFocused() {
    return this._SignInPasswordContent;
  }
}
